import { DIAGNOSTIC_LIMITS, formatTrendSummary } from './metricDiagnosticCandidates.js';

const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[c]));

export function diagnosticContextText(data, language = 'cs') {
  const context = data.auditContext;
  if (!context || !['manual', 'trend', 'failing'].includes(context.reason)) return null;
  const cs = language === 'cs';
  const trend = context.trend;
  const hasTrend = trend?.source === 'crux' && trend?.scope === 'origin'
    && trend?.windowDays === 28 && Number.isFinite(trend?.baseline)
    && Number.isFinite(trend?.value) && Number.isFinite(Date.parse(trend?.startDate))
    && Number.isFinite(Date.parse(trend?.endDate));
  return {
    title: cs ? (hasTrend ? 'Prověření zhoršující se metriky' : context.reason === 'manual' ? 'Preventivní diagnostika' : 'Důvod diagnostiky')
      : (hasTrend ? 'Investigation of a deteriorating metric' : context.reason === 'manual' ? 'Preventive diagnostic' : 'Diagnostic context'),
    trend: hasTrend ? formatTrendSummary({ metric: data.metric, trend }, language) : '',
    caveat: cs
      ? 'Tento audit měří současný stav vybrané metriky a zařízení. Historický trend sám neurčuje příčinu změny. Příčinu lze uvést jen tehdy, pokud ji zachytí platné měření; nezachycení problému nevyvrací terénní trend. Bez srovnatelného staršího měření nelze prokázat, která změna webu zhoršení způsobila.'
      : 'This audit measures the current state of the selected metric and device. The historical trend alone does not identify a cause. A cause requires valid measured evidence; failure to reproduce an issue does not disprove a field trend. Without a comparable earlier measurement, the site change responsible for deterioration cannot be established.'
  };
}

export function renderDiagnosticAuditContext(data, language = 'cs') {
  const context = diagnosticContextText(data, language);
  if (!context) return '';
  return `<aside data-diagnostic-context style="margin:20px 0;padding:16px;border:1px solid #94a3b8;border-radius:12px;line-height:1.6"><strong>${escape(context.title)} — ${escape(data.metric)} / ${escape(data.device)}</strong>${context.trend ? `<p>${escape(context.trend)}</p>` : ''}<p>${escape(context.caveat)}</p></aside>`;
}

export function preventiveReportCopy(data, language = 'cs') {
  if (!['manual', 'trend'].includes(data.auditContext?.reason)) return null;
  const limit = DIAGNOSTIC_LIMITS[data.metric]?.[0];
  if (!limit) return null;
  // Do not replace the actual failure message, and never label absent field
  // data good. This neutral introduction does not erase measured findings.
  const value = data.fieldValue;
  if (value != null && Number(value) > limit) return null;
  const context = diagnosticContextText(data, language);
  const cs = language === 'cs';
  return {
    headline: [cs ? 'Cílená diagnostika' : 'Targeted diagnostic', data.metric],
    headlineEm: 1,
    subhead: cs
      ? value == null ? 'Pro vybrané zařízení chybí terénní hodnota. Následují dostupné výsledky diagnostiky, nikoli potvrzení stavu skutečných návštěvníků.'
        : 'Vybraná terénní hodnota je stále v dobrém pásmu. Diagnostika prověřuje současný stav; sama neprokazuje příčinu historického zhoršení.'
      : value == null ? 'The selected device has no field value. Available diagnostic results follow; they do not establish real-user performance.'
        : 'The selected field value remains in the good band. This diagnostic investigates the current state; it does not by itself prove the cause of historical deterioration.',
    body: [context.trend, context.caveat].filter(Boolean),
    pullquote: cs ? 'Trend je důvod k prověření, nikoli důkaz příčiny.' : 'A trend warrants investigation; it is not proof of a cause.'
  };
}
